/**
 * Moodboard loader — typed accessor for the 97 scraped reference images.
 * Source: assets/moodboard/MANIFEST.json (created by the scrape pipeline).
 */

import manifestJson from "../assets/moodboard/MANIFEST.json";

export type MoodboardImage = {
  idx: number;
  filename: string;
  url: string;
  alt: string;
};

export type MoodboardManifest = {
  source: string;
  boardName: string;
  boardOwner: string;
  boardPinCount: number;
  scrapedCount: number;
  note: string;
  scrapedAt: string;
  scrapedVia: string;
  copyright: string;
  images: MoodboardImage[];
};

export const manifest = manifestJson as MoodboardManifest;

/**
 * Images excluded from the S11 Canon gallery. These are either:
 * - Specific references that don't fit the final pitch tone (Alfredo album)
 * - The surreal / retro-tech / mystical cluster (too off-brand for the closer)
 *
 * The full manifest stays unchanged — sections that reference specific
 * images by key (like S06, S08) still load them. The Canon gallery
 * filters against this set at render time.
 */
export const canonExcluded = new Set<number>([
  44, // Alfredo II album art
  // 72-97: trim the tail so the gallery fits the grid cleanly + surreal/
  // retro-tech/mystical end of the board
  72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90,
  91, 92, 93, 94, 95, 96, 97,
]);

/**
 * The images that should appear in the Canon gallery — pre-filtered
 * to match the final narrative tone (mafia cinema + food + Meyhem
 * persona + editorial poster design).
 */
export const canonImages = manifest.images.filter(
  (img) => !canonExcluded.has(img.idx),
);

/**
 * Get the public URL for a moodboard THUMBNAIL (600px wide WebP).
 * Use this for grid/card displays where the image doesn't need to be huge.
 * Images live in src/assets/moodboard/thumbs/ as pre-compressed WebPs.
 */
export function moodboardUrl(idx: number): string {
  const num = String(idx).padStart(2, "0");
  return new URL(`../assets/moodboard/thumbs/${num}.webp`, import.meta.url).href;
}

/**
 * Get the public URL for a moodboard LARGE image (1200px wide WebP).
 * Use this for lightbox / detail views where the image needs to be high quality.
 */
export function moodboardLargeUrl(idx: number): string {
  const num = String(idx).padStart(2, "0");
  return new URL(`../assets/moodboard/large/${num}.webp`, import.meta.url).href;
}

/**
 * Get a curated set of "key" images — hand-picked hero references by theme.
 * These indices match the analysis in BRIEF.md § 9.
 */
export const keyImages = {
  churchPew: 6, // Kendrick HUMBLE. pew — tungsten cathedral
  sopranosHorse: 7, // Sopranos surreal luxury
  satriales: 9, // Satriale's Pork Store — the anchor
  kitano: 29, // Kitano stillness
  meyhemMunchies: 34, // Meyhem real-world anchor
  blackVladimir: 36, // Meyhem canonical album cover
  alchemistEditorial: 45, // Editorial poster language
  trainspotting: 48, // Spot color halftone
  truffeNoire: 57, // Botanical product branding
  crucifixPCB: 74, // Retro-tech mysticism
  taxiDriver: 88, // Object-as-metaphor
  flameHead: 89, // Illustrated interior monologue
  comeAndSee: 95, // Multi-panel transition
  colombianHillside: 53, // Terroir / breath moment
} as const;
